import * as amplitude from "@amplitude/analytics-browser";
import {
  IconButton,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import Info from "@redotech/redo-web/icon-old/info.svg";
import { CardElement, useElements } from "@stripe/react-stripe-js";
import type { StripeElements } from "@stripe/stripe-js";
import { memo, useEffect, useState } from "react";
import * as review from "../review.module.css";

const BITTERSWEET_RED_COLOR = "#fa755a";
const CADET_BLUE_COLOR = "#aab7c4";
const MARTINIQUE_COLOR = "#32325d";

export const Payment = memo(function Payment(props: {
  nextStep: () => void;
  setElements(elements: StripeElements): void;
  setPaymentInfoFilled(filled: boolean): void;
  isInstant?: boolean;
  settings: ReturnAppSettings;
}) {
  const [showCardError, setShowCardError] = useState<boolean>(false);
  const {
    nextStep,
    setElements,
    setPaymentInfoFilled,
    isInstant = false,
    settings,
  } = props;
  const elements = useElements();
  useEffect(() => {
    amplitude.logEvent("card-info-displayed");
  }, []);
  useEffect(() => {
    if (elements) {
      setElements(elements);
    }
  }, [elements, setElements]);
  return (
    <form onSubmit={nextStep}>
      <div className={review.inputRow}>
        <div className={review.cardInformation}>
          <div>Card Information:</div>
          {isInstant && (
            <CustomTooltip
              placement="top"
              title="This card will be charged if you do not return your items."
            >
              <IconButton>
                <Info className={review.infoIcon} />
              </IconButton>
            </CustomTooltip>
          )}
        </div>
        <div className={review.cardInformationRow}>
          <CardElement
            className={review.input}
            onChange={(e) => {
              setPaymentInfoFilled(e.complete);
              setShowCardError(!e.complete);
            }}
            options={{
              style: {
                base: {
                  color: MARTINIQUE_COLOR,
                  fontSize: "16px",
                  "::placeholder": {
                    color:
                      settings.theme.custom_payment_placeholder_color ??
                      CADET_BLUE_COLOR,
                  },
                },
                invalid: {
                  color: BITTERSWEET_RED_COLOR,
                  iconColor: BITTERSWEET_RED_COLOR,
                },
              },
              hidePostalCode: true,
            }}
          />
          {showCardError && (
            <div className={review.cardInformationError}>
              Please add all credit card info
            </div>
          )}
        </div>
      </div>
    </form>
  );
});

const CustomTooltip = styled(function CustomTooltip({
  className,
  ...props
}: TooltipProps) {
  return <Tooltip {...props} classes={{ popper: className }} />;
})(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 260,
    fontSize: theme.typography.pxToRem(14),
    padding: 8,
    border: "1px solid #dadde9",
  },
}));
